import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { useSetChartMetadata } from './ChartContext';
import { resolveEventDisplayName } from '../../../../lib/constants/channelEvents';

const CATEGORY_DOT_COLORS: Record<string, string> = {
    campaign: '#6366F1',
    ad_set: '#8B5CF6',
    ad: '#EC4899',
    product: '#10B981',
    order: '#3B82F6',
    account: '#6B7280',
    price_rule: '#F59E0B',
    ad_group: '#8B5CF6',
    collection: '#14B8A6',
    article: '#06B6D4',
    blog: '#06B6D4',
    page: '#9CA3AF',
    comment: '#9CA3AF',
};

export const ActivityTimelineChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { events } = useBoardChartData();

    const sortedEvents = useMemo(() => {
        if (!events || events.length === 0) return [];
        return [...events].sort((a, b) => {
            const dateA = a.date ? new Date(a.date).getTime() : 0;
            const dateB = b.date ? new Date(b.date).getTime() : 0;
            return dateB - dateA;
        });
    }, [events]);

    const isEmpty = useMemo(() => sortedEvents.length === 0, [sortedEvents]);

    const summaryText = useMemo(() => {
        if (isEmpty) return undefined;
        return t('charts.activityTimeline.eventCount', '{{count}} events', { count: sortedEvents.length });
    }, [isEmpty, sortedEvents.length, t]);

    useSetChartMetadata({ isEmpty, summary: summaryText });

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2 overflow-y-auto">
                <ul className="space-y-1">
                    {sortedEvents.map((event, index) => {
                        const dotColor = CATEGORY_DOT_COLORS[event.category] || '#6B7280';
                        const eventDate = event.date ? format(parseISO(event.date), 'MMM d, yyyy HH:mm') : t('charts.activityTimeline.unknownDate', 'Unknown date');
                        const displayName = resolveEventDisplayName(event, t);

                        return (
                            <li
                                key={event.id || index}
                                className="flex items-start gap-3 py-1.5 px-2 rounded-md hover:bg-background-elevated transition-colors"
                            >
                                <span
                                    className="mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: dotColor }}
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="text-[11px] text-primary truncate">
                                        {displayName}
                                    </p>
                                    <p className="text-[10px] text-primary/60">
                                        {eventDate}
                                    </p>
                                </div>
                                <span
                                    className="text-[9px] px-1.5 py-0.5 rounded-md flex-shrink-0"
                                    style={{ backgroundColor: `${dotColor}20`, color: dotColor }}
                                >
                                    {String(t(`charts.activityTimeline.category.${event.category}`, event.category))}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};
